import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../../../context/AuthContext";
import { toAbsoluteUrl } from "../../../helpers/AssetHelpers/AssetHelpers";
import { EmailSentNotice } from "./EmailSentNotice";
import { validateEmail } from "../../../helpers/ValidationHelpers";
import { buildDraftKey, getDraftUserId, readSessionDraft } from "../../../helpers/SessionStorageHelpers";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { trackBehaviourEvent } from "../../../helpers/userBehaviourTrackingHelpers";

interface LoginProps {
  notice?: React.ReactNode;
  /** Prefills the email field, same as the ?email= query param but settable without a URL. */
  initialEmail?: string;
  /** Submits the form as soon as a valid initialEmail is available, so no extra click is needed
   *  (e.g. when this is opened from a trigger that already knows the visitor's email). */
  autoSubmit?: boolean;
  /** Called once the sign-in link has actually been sent, with the email it was sent to. */
  onSent?: (email: string) => void;
  /** Drops the outer card chrome (border/shadow/max-width) for use inside something that
   *  already provides its own surface, e.g. LoginModal. Defaults to false so every existing
   *  bare-page usage (AuthPage, VerifyEmail, InvalidLoggedOutFormToken) is unaffected. */
  bare?: boolean;
  /** Called when a "Sign up" link is clicked, in addition to its normal navigation - lets a
   *  container that needs to close itself first (e.g. LoginModal) do so as the page navigates
   *  away. */
  onSignUpClick?: () => void;
  /** Reports the in-flight "sending the link" state, so a container that can be dismissed
   *  independently of this form (e.g. LoginModal) can block closing while a send is in flight. */
  onLoadingChange?: (loading: boolean) => void;
  /** Overrides where a clicked sign-in link lands after auth completes, taking precedence over
   *  the ?redirect= URL param this form otherwise reads. Lets a specific trigger (e.g. "Book a
   *  time") send the visitor somewhere other than the default /dashboard, regardless of what
   *  page/URL the form happens to be rendered on - the popup modal isn't reached via its own
   *  URL, so the query-param mechanism alone can't distinguish between triggers. */
  redirectPath?: string;
}

export function Login({
  notice,
  initialEmail,
  autoSubmit,
  onSent,
  bare,
  onSignUpClick,
  onLoadingChange,
  redirectPath: redirectPathOverride,
}: LoginProps) {
  const { user, loginWithGoogle, loginWithLink }: any = useUserAuth();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") ?? "";

  // Same draft the lander funnels and Registration read - lets a returning user's email
  // populate instantly on mount without them having to remember/retype it, same as Registration.
  const sessionDraftEmail = useMemo(
    () => readSessionDraft(buildDraftKey(getDraftUserId(user)))?.data?.email ?? "",
    [user]
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [email, setEmail] = useState(emailParam || initialEmail || sessionDraftEmail);
  const [emailSent, setEmailSent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Fires the same submit path a real click would, so autoSubmit reuses all of the validation/
  // send logic below rather than duplicating it.
  useEffect(() => {
    if (autoSubmit && email && validateEmail(email)) {
      formRef.current?.requestSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignInRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);

      const formData = new FormData(e.currentTarget);
      const emailValue = formData.get("email");
      if (!emailValue || typeof emailValue !== "string" || !validateEmail(emailValue)) return;

      const rawRedirect = redirectPathOverride || searchParams.get("redirect");
      const redirectPath: string =
        !rawRedirect || rawRedirect.startsWith("//") || !rawRedirect.startsWith("/")
          ? "/dashboard"
          : rawRedirect;

      const originUrl = `${window.location.origin}/auth/verify-email?redirect=${encodeURI(redirectPath)}`;

      window.localStorage.setItem("emailForSignIn", emailValue);

      await loginWithLink({ email: emailValue, originUrl });
      // Only sends for a visit that started on a tracked lander in this page load.
      trackBehaviourEvent("Sign In Link Sent", { page: "login" });

      setEmailSent(true);
      setLoading(false);
      onSent?.(emailValue);
    } catch (error: unknown) {
      // error status text set in api endpoint
      if (axios.isAxiosError(error)) {
        const statusText = error.response?.data || null;
        setSignInError(statusText);
      }
      window.localStorage.removeItem("emailForSignIn");
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    setEmail(value);
    setSignInError(null);
    setLoading(false);

    if (!formError) return;

    if (validateEmail(value)) {
      setFormError(null);
    } else {
      setFormError("Please enter a valid email address.");
    }
  };

  const handleBlur = () => {
    setSignInError(null);
    setLoading(false);
    if (email && !validateEmail(email)) {
      setFormError("Please enter a valid email address.");
    }
  };

  if (emailSent) {
    return <EmailSentNotice email={email} onResend={() => setEmailSent(false)} />;
  }

  const formContent = (
    <>
        <p className="text-dark fs-1 fw-bolder m-0 text-center">Sign In</p>
        {notice}
        <button
          onClick={async (e) => {
            if (await loginWithGoogle(e)) trackBehaviourEvent("Signed In", { method: "google", page: "login" });
          }}
          className="btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100"
        >
          <img
            alt="Logo"
            src={toAbsoluteUrl("/media/svg/brand-logos/google-icon.svg")}
            className="h-15px me-3"
          />
          Sign in with Google
        </button>
        {signInError &&
          <div className="alert alert-danger">
            <div className="alert-text text-center d-flex flex-column">
              <span>{signInError}</span>
              {signInError.toLowerCase().includes("account not found") &&
                <span>{" "}Please continue to{" "}<Link to="/auth/registration" className="link-primary" onClick={onSignUpClick}>
                  Sign up
                </Link></span>}
            </div>
          </div>
        }
        <form ref={formRef} onSubmit={handleSignInRequest} className="">
          <div className="d-flex flex-column gap-2 mb-10">
            <label className="form-label fw-bolder text-dark fs-6 m-0">Email</label>
            <input
              value={email}
              onChange={handleChange}
              onBlur={handleBlur}
              type="email"
              name="email"
              className={`form-control bg-transparent fs-4 ${formError ? "border-1 border-danger text-danger" : ""}`}
              autoComplete="email"
              required
            />
            {formError &&
              <span className="text-danger">{formError}</span>
            }</div>

          <button
            type="submit"
            className="btn btn-lg btn-primary w-100 mb-5 position-relative"
            disabled={formError !== null || loading}
          >{!loading ? (
            <span>Send Sign in Link</span>
          ) : (
            <><span>Sending...</span>
              <div className="spinner-border position-absolute" role="status" style={{ height: "25px", width: "25px", right: "15px" }}>
                <span className="visually-hidden">Loading...</span>
              </div></>
          )}
          </button>
        </form>
        <div className="text-gray-500 text-center fw-semibold fs-6">
          <span>Don't have an account? </span>
          <Link to="/auth/registration" className="link-primary" onClick={onSignUpClick}>
            Sign up
          </Link>
        </div>
    </>
  );

  if (bare) {
    return <div className="d-flex flex-column gap-10">{formContent}</div>;
  }

  return (
    <div className="card card-rounded w-100" style={{ maxWidth: "600px" }}>
      <div className="card-body d-flex flex-column gap-10">
        {formContent}
      </div>
    </div>
  );
}
